import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["moreTitle", "autoplayTime"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RecommendationsForLearningContent } from '../../recommendations';
import styled, { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import { useEffect, useMemo, useState } from 'react';
import { H4, H5 } from 'UIComponents/elements/headings';
import { getLearningContentItemUrl } from '../../learningContent';
import useNavigate from '../../../hooks/useNavigate';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import UsageTracker from '../../../utils/UsageTracker';
import { useVideoPlayerContext } from '../../video/components/VideoPlayer';
import { useAiRecommendationsForLearningContent } from '../../academyAi/academyAiHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_AUTOPLAY_TIME = 10;
const Wrapper = styled(UIFlex).attrs({
  justify: 'center'
}).withConfig({
  displayName: "VideoRecommendationsForLearningContent__Wrapper",
  componentId: "wmgiwc-0"
})(["height:100%;padding:60px;padding-top:7%;width:100%;.content-card{margin-bottom:0;}"]);
const MobileWrapper = styled(UIFlex).attrs({
  align: 'center',
  justify: 'center'
}).withConfig({
  displayName: "VideoRecommendationsForLearningContent__MobileWrapper",
  componentId: "wmgiwc-1"
})(["height:100%;width:100%;padding:52px;.reduced-card{max-width:400px;}"]);
const useMainRecommendationUrl = item => {
  const {
    recommendations
  } = useAiRecommendationsForLearningContent({
    item,
    size: 1
  });
  const recommendedItem = recommendations && recommendations.length && recommendations[0];
  const {
    url
  } = recommendedItem ? getLearningContentItemUrl(recommendedItem) : {
    url: null
  };
  return {
    url,
    recommendedItem
  };
};
const CloseRecommendationsButton = ({
  item: {
    id,
    language,
    type
  },
  onClick,
  closeType,
  className
}) => {
  const handleOnCancel = () => {
    UsageTracker.track('interaction', {
      action: `cancel-recommendation-${closeType}`,
      id,
      contentType: type,
      language
    });
    if (onClick) onClick();
  };
  return /*#__PURE__*/_jsx(UIButton, {
    className: `m-top-0 ${className}`,
    onClick: handleOnCancel,
    size: closeType === 'mobile' ? 'small' : 'medium',
    style: {
      width: 'auto'
    },
    use: "secondary-ghost",
    children: closeType === 'autoplay' ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.recommendations.seeMore"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.common.close"
    })
  });
};
const MainRecommendation = ({
  onClickCancel,
  autoplayTime,
  item,
  onItemClick
}) => {
  const {
    url,
    recommendedItem
  } = useMainRecommendationUrl(item);
  const navigate = useNavigate();
  const [remainingSeconds, setRemainingSeconds] = useState(autoplayTime);
  const trackInfo = useMemo(() => {
    return {
      id: item.id,
      contentType: item.type,
      language: item.language
    };
  }, [item]);
  useEffect(() => {
    if (!url) return;
    let interval = undefined;
    const checkIfAutoplay = () => {
      if (remainingSeconds > 0) return;
      clearInterval(interval);
      if (recommendedItem) onItemClick(recommendedItem, 'autoplay');
      navigate(url);
    };
    interval = setInterval(() => {
      setRemainingSeconds(remainingSeconds - 1);
    }, 1000);
    checkIfAutoplay();
    return () => {
      clearInterval(interval);
    };
  }, [navigate, onItemClick, recommendedItem, remainingSeconds, trackInfo, url]);
  if (!url || !recommendedItem) return null;
  return /*#__PURE__*/_jsxs("div", {
    style: {
      maxWidth: 380,
      width: '45%'
    },
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.recommendations.upNextTimer",
      options: {
        remainingSeconds
      }
      // @ts-expect-error Untyped
      ,
      style: {
        marginBottom: 16,
        display: 'block'
      }
    }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, Object.assign({
      appearsIn: "main-video-recommendation",
      onItemClick: () => onItemClick(recommendedItem, 'main'),
      showDescription: false,
      use: "on-dark"
    }, item)), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIRouterButton, {
        onClick: () => onItemClick(recommendedItem, 'watch-now'),
        to: url,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.recommendations.watchNow"
        })
      }), /*#__PURE__*/_jsx(CloseRecommendationsButton, {
        closeType: "autoplay",
        item: item,
        onClick: onClickCancel
      })]
    })]
  });
};
const MoreRecommendations = ({
  moreTitle,
  onClickCancel,
  item,
  onItemClick
}) => {
  const {
    id,
    language,
    type
  } = item;
  return /*#__PURE__*/_jsxs("div", {
    style: {
      width: '100%'
    },
    children: [/*#__PURE__*/_jsx(H4, {
      className: "m-bottom-4",
      children: moreTitle
    }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
      appearsIn: "more-video-recommendations",
      id: id,
      language: language,
      onItemClick: recommendedItem => onItemClick(recommendedItem, 'more'),
      showDescription: false,
      type: type,
      use: "on-dark"
    }), /*#__PURE__*/_jsx(CloseRecommendationsButton, {
      closeType: "more",
      item: item,
      onClick: onClickCancel
    })]
  });
};
const MobileRecommendations = ({
  onClickCancel,
  item,
  onItemClick
}) => {
  const {
    id,
    language,
    type
  } = item;
  const {
    url,
    recommendedItem
  } = useMainRecommendationUrl(item);
  if (!url || !recommendedItem) return null;
  return /*#__PURE__*/_jsx(MobileWrapper, {
    children: /*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.recommendations.upNext"
        })
      }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
        appearsIn: "more-video-recommendations-mobile",
        id: id,
        language: language,
        onItemClick: () => onItemClick(recommendedItem, 'mobile-main'),
        showDescription: false,
        size: "small",
        type: type,
        use: "on-dark"
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(UIRouterButton, {
          onClick: () => onItemClick(recommendedItem, 'mobile-watch-now'),
          size: "small",
          style: {
            width: 'auto'
          },
          to: url,
          use: "tertiary-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.recommendations.watchNow"
          })
        }), /*#__PURE__*/_jsx(CloseRecommendationsButton, {
          className: "m-left-3",
          closeType: "mobile",
          item: item,
          onClick: onClickCancel
        })]
      })]
    })
  });
};
export const VideoRecommendationsForLearningContent = _ref => {
  let {
      moreTitle,
      autoplayTime = DEFAULT_AUTOPLAY_TIME
    } = _ref,
    item = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    hideCompletedScreen
  } = useVideoPlayerContext();
  const [hasCanceled, setHasCanceled] = useState(false);
  const {
    isDesktop
  } = useMediaQuery();
  const handleCloseRecommendations = () => hideCompletedScreen();
  const handleOnItemClick = (recommendedItem, type) => {
    UsageTracker.track('interaction', {
      action: `${type}-recommendation`,
      recommendationType: recommendedItem && recommendedItem.type,
      item: recommendedItem && recommendedItem.id,
      id: item.id,
      contentType: item.type,
      language: item.language
    });
    hideCompletedScreen();
  };
  if (!isDesktop) return /*#__PURE__*/_jsx(MobileRecommendations, {
    item: item,
    onClickCancel: handleCloseRecommendations,
    onItemClick: handleOnItemClick
  });
  return /*#__PURE__*/_jsx(Wrapper, {
    children: hasCanceled ? /*#__PURE__*/_jsx(MoreRecommendations, {
      item: item,
      moreTitle: moreTitle,
      onClickCancel: handleCloseRecommendations,
      onItemClick: handleOnItemClick
    }) : /*#__PURE__*/_jsx(MainRecommendation, {
      autoplayTime: autoplayTime,
      item: item,
      onClickCancel: () => setHasCanceled(true),
      onItemClick: handleOnItemClick
    })
  });
};
export default VideoRecommendationsForLearningContent;